<!-- app-subdomain-creators/src/Components/NewsfeedCreatorDiscussionTab.vue -->

<template>
  
  <!-- discussion -->
  <div class="h-full pt-4 rounded w-full pb-10">
    <div class="rounded lg:w-[1175px] mx-auto flex gap-4">
      <!-- left side -->
      <div class="w-[60%]">
        <!-- create post box -->
        <div
          class="w-full rounded-xl border-[1px] pt-3 pb-2 px-4 border-zinc-200 shadow-sm bg-white"
        >
          <div
            class="w-full flex items-center gap-2 border-b border-b-zinc-200 pb-4"
          >
            <div
              v-if="isCreatePostCardLoading"
              class="inline-block h-10 w-10 rounded-full ring-2 ring-white bg-zinc-200 animate-pulse"
            ></div>
            <template v-else>
              <img
                v-for="(avatar, index) in postavatar"
                :key="index"
                :src="avatar.src"
                :class="[
                  'inline-block h-10 w-10 rounded-full ring-2 ring-white',
                  'z-' + (postavatar.length - index)
                ]"
                :alt="'Post Avatar ' + (index + 1)"
              />
            </template>

            <div
              v-if="isCreatePostCardLoading"
              class="rounded-full w-full h-10 flex items-center px-4 bg-zinc-100"
            >
              <div class="h-4 w-44 rounded bg-zinc-200 animate-pulse"></div>
            </div>
            <div
              v-else
              @click="handleOpenCreatePost"
              class="rounded-full w-full h-10 flex items-center px-4 bg-[#f0f3f8] hover:bg-[#e7eaf0] cursor-pointer"
            >
              <h4 class="text-zinc-500 text-[16px] font-base">
                Write something...
              </h4>
            </div>
          </div>
          <div
            class="w-full pt-2 pb-0 justify-between flex items-center gap-4"
          >
            <!-- photo/video button -->
            <div
              v-if="isCreatePostCardLoading"
              class="flex justify-center items-center rounded-lg h-full w-full py-2"
            >
              <div class="h-5 w-28 rounded bg-zinc-200 animate-pulse"></div>
            </div>
            <div
              v-else
              @click="handleOpenCreatePost"
              class="flex justify-center items-center rounded-lg hover:bg-zinc-100 cursor-pointer h-full w-full py-2"
            >
              <div class="flex items-center gap-2">
                <img
                  class="w-6 h-6"
                  :src="photoVideoIcon"
                  alt="Photo/Video Icon"
                />
                <h4 class="text-zinc-500 text-base font-medium">
                  Photo/Video
                </h4>
              </div>
            </div>

            <!-- poll button -->
            <div
              v-if="isCreatePostCardLoading"
              class="flex justify-center items-center rounded-lg h-full w-full py-2"
            >
              <div class="h-5 w-20 rounded bg-zinc-200 animate-pulse"></div>
            </div>
            <div
              v-else
              @click="handleOpenCreatePoll"
              class="flex justify-center items-center rounded-lg hover:bg-zinc-100 cursor-pointer h-full w-full py-2"
            >
              <div class="flex items-center gap-2">
                <img
                  class="w-6 h-6"
                  :src="pollIcon"
                  alt="Poll Icon"
                />
                <h4 class="text-zinc-500 text-base font-medium">Poll</h4>
              </div>
            </div>
          </div>
        </div>

               <!-- posts list -->
            <div class="mt-4 min-h-[220px]">
              <!-- Loading / empty states -->
              <div
                v-if="(!hasLoadedOnce && !posts.length) || (isLoadingPosts && !posts.length)"
                class="space-y-3"
              >
                <div
                  v-for="n in 3"
                  :key="`discussion-skeleton-${n}`"
                  class="h-40 rounded-xl bg-gradient-to-r from-zinc-200 via-zinc-100 to-zinc-200 animate-pulse"
                ></div>
              </div>

              <div
                v-else-if="hasLoadedOnce && !posts.length"
                class="text-sm text-zinc-400"
              >
                No posts yet. Be the first to post!
              </div>

              <!-- Virtualized posts list -->
              <div v-else ref="postsListRef" class="space-y-3">
                <!-- Top spacer to represent posts above the window -->
                <div v-if="useVirtualization" :style="{ height: topSpacerHeight + 'px' }"></div>

                <!-- Visible window of posts -->
                <NewsfeedCreatorPost
                  v-for="post in visiblePosts"
                  :key="post.id"
                  :post="post"
                  :postingAsName="postingAsName"
                  :postingAsAvatarUrl="postingAsAvatarUrl"
                  context="discussion"
                  :featureInstanceId="featureInstanceId"
                  @edit-post="handleEditPost"
                  @delete-post="handleDeletePost"
                  @save-post="handleSavePostFromDiscussion"
                  @unsave-post="handleUnsavePostFromDiscussion"
                  @pin-changed="handlePinChanged"
                  @media-loaded="handlePostMediaLoaded"
                  @open-full-post="handleOpenFullPost"
                  @post-visible="handlePostVisible"
                />

                <!-- Bottom spacer to represent posts below the window -->
                <div v-if="useVirtualization" :style="{ height: bottomSpacerHeight + 'px' }"></div>
                <div
                  v-if="isLoadingPosts && posts.length"
                  class="space-y-3"
                >
                  <div
                    v-for="n in 2"
                    :key="`discussion-page-skeleton-${n}`"
                    class="h-32 rounded-xl bg-gradient-to-r from-zinc-200 via-zinc-100 to-zinc-200 animate-pulse"
                  ></div>
                </div>
                <div
                    v-if="hasMorePosts && posts.length"
                    ref="infiniteScrollSentinel"
                    class="h-10"
                  ></div>

              </div>

              

            </div>





      </div>

      <!-- right side -->
      <div class="w-[40%]">
        <!-- group info box -->
        <div
          class="group w-full rounded-xl border-[1px] p-3 border-zinc-200 shadow-sm bg-white"
        >
          <div class="flex items-center justify-between pb-3">
            <h4 class="text-zinc-800 text-base font-medium">About</h4>
            <button
              v-if="!isAboutLoading && hasAboutDescription && !isEditingAbout"
              type="button"
              class="opacity-0 group-hover:opacity-100 transition-opacity duration-200 inline-flex items-center gap-1 px-3 py-1.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 text-sm font-semibold rounded-lg"
              @click="startEditAbout"
            >
              <PencilIcon class="w-6 h-6" />
              Edit
            </button>
          </div>

          <div class="pb-4 space-y-3">
            <div class="flex items-start gap-3">
              <LockClosedIcon class="w-6 h-6 text-zinc-800" />
              <div>
                <p class="text-zinc-800 font-semibold text-base leading-tight">Private</p>
                <p class="text-zinc-800 text-sm leading-snug">
                  Only members can see who's in the group and what they post.
                </p>
              </div>
            </div>

            <div class="flex items-start gap-3">
              <EyeSlashIcon class="w-6 h-6 text-zinc-800" />
              <div>
                <p class="text-zinc-800 font-semibold text-base leading-tight">Hidden</p>
                <p class="text-zinc-800 text-sm leading-snug">
                  Only members can find this group.
                </p>
              </div>
            </div>
          </div>

          <!-- About skeleton -->
          <div v-if="isAboutLoading" class="space-y-2 ">
            <div class="h-4 rounded bg-zinc-200 animate-pulse w-[90%]"></div>
            <div class="h-4 rounded bg-zinc-200 animate-pulse w-[82%]"></div>
            <div class="h-4 rounded bg-zinc-200 animate-pulse w-[95%]"></div>
            <div class="h-4 rounded bg-zinc-200 animate-pulse w-[70%]"></div>
          </div>

          <!-- Edit mode -->
          <div v-else-if="isEditingAbout">
            <textarea
              v-model="aboutDraft"
              rows="7"
              class="w-full rounded-xl bg-zinc-100 px-3 py-2 text-sm text-zinc-800 placeholder:text-zinc-400 border-none outline-none resize-none min-h-[450px] max-h-[600px] app-scroll focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
              placeholder="Tell members what this group is about and add rules..."
            ></textarea>

            <p
              v-if="aboutError"
              class="text-xs text-red-600 mt-2"
            >
              {{ aboutError }}
            </p>

            <div class="mt-3 flex items-center justify-end gap-2">
              <button
                type="button"
                class="px-3 py-1.5 text-sm font-semibold text-zinc-600 hover:text-zinc-800"
                :disabled="isSavingAbout"
                @click="cancelEditAbout"
              >
                Cancel
              </button>
              <button
                type="button"
                class="px-4 py-1.5 text-sm font-semibold rounded-lg text-white bg-[#2D8CFF] hover:bg-[#1f7aeb] disabled:bg-[#9bbef3]"
                :disabled="isSavingAbout"
                @click="saveAboutDescription"
              >
                {{ isSavingAbout ? 'Saving...' : 'Save' }}
              </button>
            </div>
          </div>

          <!-- Empty state -->
          <div
            v-else-if="!hasAboutDescription"
            class="py-7 px-6 text-center"
          >
            <div class="mx-auto  flex items-center justify-center mb-3">
              <DocumentTextIcon class="w-20 text-[#2D8CFF]" />
            </div>
            <h5 class="text-zinc-700 text-xl leading-tight font-semibold">You don't have your about section setup.</h5>
            <p class="mt-2.5 text-zinc-500 text-sm leading-[1.45] max-w-[360px] mx-auto">
              Add a short description so members know what this group is about, and list some rules to help set the tone for your group, and help prevent member conflict. Write up to 10 rules about your group..
            </p>
            <button
              type="button"
              class="mt-5 h-10 w-[180px] rounded-xl bg-[#2D8CFF] hover:bg-[#1f7aeb] text-white text-sm font-semibold"
              @click="startEditAbout"
            >
              Get started
            </button>
          </div>

          <!-- Read mode -->
          <p
            v-else
            class="text-zinc-600 text-sm font-medium whitespace-pre-line break-words px-2"
          >
            {{ aboutDescription }}
          </p>
        </div>
      </div>
    </div>
  </div>

<NewsfeedDeletePostConfirmModal
  :is-open="deleteModalOpen"
  :is-submitting="isDeletingPost"
  @cancel="handleCancelDelete"
  @confirm="handleConfirmDelete"
/>

<NewsFeedFullPostSlug
  :is-open="isFullPostOpen"
  :post="selectedFullPost"
  :posting-as-name="postingAsName"
  :posting-as-avatar-url="postingAsAvatarUrl"
  :feature-instance-id="props.featureInstanceId"
  @close="handleCloseFullPost"
/>

</template>

<script setup>
import {
  ref,
  defineEmits,
  defineProps,
  onMounted,
  onBeforeUnmount,
  watch,
  computed,
  nextTick,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useWebsocketStore } from '@/stores/websocketStore';

import NewsfeedCreatorPost from '@/Components/NewsfeedCreatorPost.vue';
import NewsfeedDeletePostConfirmModal from '@/Components/NewsfeedDeletePostConfirmModal.vue';
import NewsFeedFullPostSlug from '@/Components/NewsFeedFullPostSlug.vue';
import { PencilIcon } from '@heroicons/vue/24/outline';
import { DocumentTextIcon, LockClosedIcon, EyeSlashIcon } from '@heroicons/vue/24/solid';


const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
const router = useRouter();
const route = useRoute();
const websocketStore = useWebsocketStore();

let unsubscribeNewsfeedPostCreated = null;
let unsubscribeNewsfeedPostDeleted = null;
let unsubscribeNewsfeedCommentCreated = null;
let unsubscribeNewsfeedCommentDeleted = null;
let unsubscribeNewsfeedPostReactionUpdated = null;
let unsubscribeNewsfeedPostReactionToast = null;
let liveReloadTimer = null;
const seenWsPostIds = new Set();
const seenWsCommentIds = new Set();
const seenWsPostReactionKeys = new Set();
const trackedPostViewKeys = new Set();

const props = defineProps({
  featureInstanceId: {
    type: [Number, String],
    default: null,
  },
  postingAsName: {
    type: String,
    default: 'You',
  },
  postingAsAvatarUrl: {
    type: String,
    default: null,
  },
  openPostSlug: {
    type: String,
    default: '',
  },
});
const emit = defineEmits(['open-create-post', 'open-edit-post']);

const aboutDescription = ref('');
const aboutDraft = ref('');
const isAboutLoading = ref(false);
const isSavingAbout = ref(false);
const isEditingAbout = ref(false);
const aboutError = ref('');

const hasAboutDescription = computed(() => {
  return String(aboutDescription.value || '').trim().length > 0;
});

const startEditAbout = () => {
  aboutError.value = '';
  aboutDraft.value = String(aboutDescription.value || '');
  isEditingAbout.value = true;
};

const cancelEditAbout = () => {
  if (isSavingAbout.value) return;
  aboutError.value = '';
  aboutDraft.value = String(aboutDescription.value || '');
  isEditingAbout.value = false;
};

const loadAboutDescription = async () => {
  if (!props.featureInstanceId) {
    aboutDescription.value = '';
    aboutDraft.value = '';
    isEditingAbout.value = false;
    return;
  }

  isAboutLoading.value = true;
  aboutError.value = '';

  try {
    const params = new URLSearchParams({
      featureInstanceId: String(props.featureInstanceId),
    });

    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/instance-about-description?${params.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Failed to load about description (${res.status})`);
    }

    const data = await res.json().catch(() => ({}));
    const nextValue = String(data?.aboutDescription || '').trim();

    aboutDescription.value = nextValue;
    if (!isEditingAbout.value) {
      aboutDraft.value = nextValue;
    }
  } catch (err) {
    console.error('[NewsfeedCreatorDiscussionTab] loadAboutDescription error:', err);
    aboutError.value = 'Failed to load About section.';
    aboutDescription.value = '';
  } finally {
    isAboutLoading.value = false;
  }
};

const saveAboutDescription = async () => {
  if (!props.featureInstanceId) return;
  if (isSavingAbout.value) return;

  isSavingAbout.value = true;
  aboutError.value = '';

  try {
    const payload = {
      featureInstanceId: Number(props.featureInstanceId),
      aboutDescription: String(aboutDraft.value || ''),
    };

    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/instance-about-description`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
    );

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Failed to save about description (${res.status})`);
    }

    const data = await res.json().catch(() => ({}));
    const nextValue = String(data?.aboutDescription || aboutDraft.value || '').trim();
    aboutDescription.value = nextValue;
    aboutDraft.value = nextValue;
    isEditingAbout.value = false;
  } catch (err) {
    console.error('[NewsfeedCreatorDiscussionTab] saveAboutDescription error:', err);
    aboutError.value = 'Failed to save About section.';
  } finally {
    isSavingAbout.value = false;
  }
};


const isFullPostOpen = ref(false);
const selectedFullPost = ref(null);
const isLoadingDeepLinkPost = ref(false);
const lastOpenedSlug = ref('');

const defaultAvatar = new URL(
  '@/assets/media/images/default-avatar.png',
  import.meta.url
).href;

// Avatar row above "Write something..."
// ⬇️ Don't fall back to default immediately, wait for a real postingAsAvatarUrl
const postavatar = computed(() => {
  if (!props.postingAsAvatarUrl) {
    // While the settings / posting-as avatar are still loading,
    // render nothing so we don't flash the default woman avatar.
    return [];
  }

  return [
    {
      src: props.postingAsAvatarUrl,
    },
  ];
});

const isCreatePostCardLoading = computed(() => !props.postingAsAvatarUrl);


const photoVideoIcon = new URL(
  '@/assets/media/images/video-image-newsfeed-post-icon.png',
  import.meta.url
).href;

const pollIcon = new URL(
  '@/assets/media/images/video-image-newsfeed-poll-icon.png',
  import.meta.url
).href;

const handleOpenCreatePost = () => {
  emit('open-create-post');
};

const handleOpenCreatePoll = () => {
  emit('open-create-post', { startWithPoll: true });
};

async function trackPostView(postId, source = 'feed') {
  const numericPostId = Number(postId);
  if (!Number.isFinite(numericPostId) || numericPostId <= 0) return;

  const normalizedSource = source === 'slug' ? 'slug' : 'feed';
  const dedupeKey = `${numericPostId}:${normalizedSource}`;
  const shouldDedupe = normalizedSource === 'feed';
  if (shouldDedupe && trackedPostViewKeys.has(dedupeKey)) return;
  if (shouldDedupe) trackedPostViewKeys.add(dedupeKey);

  try {
    await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(numericPostId)}/view`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: normalizedSource }),
      }
    );
  } catch (err) {
    if (shouldDedupe) trackedPostViewKeys.delete(dedupeKey);
    console.error('[NewsfeedCreatorDiscussionTab] trackPostView error:', err);
  }
}

function handlePostVisible(payload) {
  const postId = Number(payload?.postId);
  if (!Number.isFinite(postId) || postId <= 0) return;
  trackPostView(postId, 'feed');
}

const handleOpenFullPost = (payload) => {
  const p = payload?.post || null;
  if (!p) return;

  selectedFullPost.value = p;
  isFullPostOpen.value = true;
  trackPostView(p.id, 'slug');

  const slug = String(p.slug || '').trim();
  if (!slug) return;

  const currentSlug = String(route.params?.postSlug || '').trim();
  if (currentSlug === slug) return;

  router.push({ name: 'NewsfeedPostSlug', params: { postSlug: slug } }).catch(() => {});
};

const handleCloseFullPost = () => {
  isFullPostOpen.value = false;
  selectedFullPost.value = null;

  const currentSlug = String(route.params?.postSlug || '').trim();
  if (!currentSlug) return;

  router.replace({ name: 'Newsfeed' }).catch(() => {});
};


/* ─────────────────────────────────────────────
 * 🔹 Posts state
 * ───────────────────────────────────────────*/
const posts = ref([]);
const isLoadingPosts = ref(false);
const hasLoadedOnce = ref(false);
const postsError = ref(null);

// simple pagination primitives (we can extend later)
const limit = 25;
const offset = ref(0);
const hasMorePosts = ref(true);
let isPaging = false;

/* ─────────────────────────────────────────────
 * ✅ Virtualization (stable)
 * ───────────────────────────────────────────*/
const VIRTUALIZATION_THRESHOLD = 30;
const VIRTUALIZATION_OVERSCAN = 15;
const VIRTUAL_WINDOW_HYSTERESIS_ITEMS = 4;
const VIRTUAL_RANGE_MIN_INTERVAL_MS = 90;

// ✅ Only two post size buckets:
// - NO media (text-only)
// - HAS media (1+ attachments / cover)
const POST_HEIGHT_NO_MEDIA = 200;   // tune this to your real "text-only" posts
const POST_HEIGHT_WITH_MEDIA = 700; // tune this to your real "media" posts

function estimatePostHeight(post) {
  const hasMedia =
    !!(post && post.hasMedia) ||
    (post && Array.isArray(post.attachments) && post.attachments.length > 0) ||
    !!(post && post.coverMediaFileId);

  return hasMedia ? POST_HEIGHT_WITH_MEDIA : POST_HEIGHT_NO_MEDIA;
}


const scrollContainerEl = ref(null);
const postsListRef = ref(null);

// ✅ stable offset within scroll container content (NOT viewport)
const postsListOffsetTop = ref(0);

const visibleRange = ref({ start: 0, end: 0 });
const useVirtualization = computed(
  () => posts.value.length > VIRTUALIZATION_THRESHOLD
);

const visiblePosts = computed(() => {
  const total = posts.value.length;
  if (!useVirtualization.value || total === 0) return posts.value;

  const start = Math.max(0, Math.min(visibleRange.value.start ?? 0, total));
  const end = Math.max(start, Math.min(visibleRange.value.end ?? total, total));

  // Safety net: never render empty due to bad range
  if (end <= start) return posts.value;

  return posts.value.slice(start, end);
});

const heightsPrefix = computed(() => {
  // prefix[i] = total height of posts[0..i-1]
  const prefix = new Array(posts.value.length + 1);
  prefix[0] = 0;
  for (let i = 0; i < posts.value.length; i++) {
    prefix[i + 1] = prefix[i] + estimatePostHeight(posts.value[i]);
  }
  return prefix;
});


const topSpacerHeight = computed(() => {
  if (!useVirtualization.value) return 0;
  const start = Math.max(0, Math.min(visibleRange.value.start ?? 0, posts.value.length));
  return heightsPrefix.value[start] || 0;
});

const bottomSpacerHeight = computed(() => {
  if (!useVirtualization.value) return 0;
  const total = posts.value.length;
  const end = Math.max(0, Math.min(visibleRange.value.end ?? 0, total));
  const totalHeight = heightsPrefix.value[total] || 0;
  const endHeight = heightsPrefix.value[end] || 0;
  return Math.max(0, totalHeight - endHeight);
});


/**
 * ✅ Get element's offsetTop relative to a scroll container's content box
 * This is stable during scrolling (unlike getBoundingClientRect diffs).
 */
function getOffsetTopWithinContainer(el, container) {
  if (!el || !container) return 0;

  let top = 0;
  let node = el;

  // Walk up offsetParents until we hit the container
  while (node && node !== container) {
    top += node.offsetTop || 0;
    node = node.offsetParent;
  }

  return top;
}

const measurePostsOffsetTop = () => {
  if (!scrollContainerEl.value || !postsListRef.value) return;
  postsListOffsetTop.value = getOffsetTopWithinContainer(
    postsListRef.value,
    scrollContainerEl.value
  );
};

const updateVisibleRange = () => {
  const total = posts.value.length;

  if (!scrollContainerEl.value || !postsListRef.value || total === 0) {
    visibleRange.value = { start: 0, end: total };
    return;
  }

  if (!useVirtualization.value) {
    visibleRange.value = { start: 0, end: total };
    return;
  }

  const container = scrollContainerEl.value;

  // ✅ Ensure we have the list offset measured
  if (!Number.isFinite(postsListOffsetTop.value) || postsListOffsetTop.value <= 0) {
    measurePostsOffsetTop();
  }

  const scrollTop = container.scrollTop;
  const viewportHeight = container.clientHeight;

  // ✅ relative scroll into the posts list (and clamp it)
  let relativeScroll = scrollTop - postsListOffsetTop.value;
  if (!Number.isFinite(relativeScroll)) relativeScroll = 0;
  if (relativeScroll < 0) relativeScroll = 0;

  const prefix = heightsPrefix.value; // length = total + 1
  const totalHeight = Number(prefix[total]) || 0;

  // If our estimates say the list is shorter than where we are, clamp.
  if (totalHeight > 0 && relativeScroll > totalHeight) {
    relativeScroll = totalHeight;
  }

  // ✅ lowerBound: first index i such that prefix[i] >= x
  const lowerBound = (arr, x) => {
    let lo = 0;
    let hi = arr.length; // IMPORTANT: arr.length = total + 1
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if ((arr[mid] || 0) < x) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  };

  // first visible item index:
  // prefix[first] <= relativeScroll < prefix[first+1]
  let first = lowerBound(prefix, relativeScroll) - 1;
  if (!Number.isFinite(first)) first = 0;
  if (first < 0) first = 0;
  if (first > total - 1) first = total - 1;

  // bottom of viewport in "estimated height space"
  const targetBottom = Math.min(totalHeight, relativeScroll + viewportHeight);

  // last visible boundary as an item count (0..total)
  let lastBoundary = lowerBound(prefix, targetBottom);
  if (!Number.isFinite(lastBoundary)) lastBoundary = total;

  // We want "end" to be an index in [0..total]
  // Guarantee we include at least one item after first.
  let endBase = Math.max(first + 1, Math.min(total, lastBoundary));

  const start = Math.max(0, first - VIRTUALIZATION_OVERSCAN);
  const end = Math.min(total, endBase + VIRTUALIZATION_OVERSCAN);

  // ✅ HARD GUARANTEE: never allow empty window
  if (end <= start) {
    const safeStart = Math.max(0, Math.min(first, total - 1));
    const safeEnd = Math.min(total, safeStart + 1);
    visibleRange.value = { start: safeStart, end: safeEnd };
    return;
  }

  const currentStart = Number(visibleRange.value?.start || 0);
  const currentEnd = Number(visibleRange.value?.end || 0);
  if (
    currentEnd > currentStart &&
    Math.abs(start - currentStart) < VIRTUAL_WINDOW_HYSTERESIS_ITEMS &&
    Math.abs(end - currentEnd) < VIRTUAL_WINDOW_HYSTERESIS_ITEMS
  ) {
    return;
  }

  visibleRange.value = { start, end };
};



// ✅ rAF throttle so scroll stays smooth even with lots of posts
let rafScrollHandle = 0;
let visibleRangeTimer = 0;
let lastVisibleRangeCalcAt = 0;
const handleScrollForVirtualization = () => {
  if (rafScrollHandle || visibleRangeTimer) return;

  const now = Date.now();
  const elapsed = now - lastVisibleRangeCalcAt;
  const delay = Math.max(0, VIRTUAL_RANGE_MIN_INTERVAL_MS - elapsed);

  const run = () => {
    visibleRangeTimer = 0;
    rafScrollHandle = requestAnimationFrame(() => {
      rafScrollHandle = 0;
      lastVisibleRangeCalcAt = Date.now();
      updateVisibleRange();
    });
  };

  if (delay > 0) {
    visibleRangeTimer = window.setTimeout(run, delay);
    return;
  }
  run();
};

let mediaRaf = 0;
const handlePostMediaLoaded = () => {
  if (mediaRaf) return;
  mediaRaf = requestAnimationFrame(() => {
    mediaRaf = 0;
    measurePostsOffsetTop();
    updateVisibleRange();
  });
};

// 🔹 Delete modal state
const deleteModalOpen = ref(false);
const deleteTargetPostId = ref(null);
const isDeletingPost = ref(false); // controls "Deleting..." button text

/**
 * Build a cover-image URL for a given media file id
 * Goes through the same Vue BFF proxy you already use:
 *   GET /api/image/:fileId → backend /api/media/image/:fileId
 */
const buildCoverImageUrl = (fileId) => {
  if (!fileId) return null;
  return `${apiProxyUrl}/api/image/${fileId}`;
};

function normalizeBackendPost(p, savedIdsSet = new Set()) {
  const currentUserName = props.postingAsName || 'You';
  const currentUserAvatar = props.postingAsAvatarUrl || defaultAvatar;

  const isPinned = !!(p.isPinned ?? p.is_pinned);

  const attachments = Array.isArray(p.attachments)
    ? p.attachments
        .map((att) => {
          if (!att) return null;
          const fileId = att.fileId || att.file_id || att.mediaFileId || null;
          if (!fileId) return null;
          return { ...att, fileId: String(fileId) };
        })
        .filter(Boolean)
    : [];

  const coverMediaFileId =
    p.coverMediaFileId ||
    p.cover_media_file_id ||
    (attachments.length ? attachments[0].fileId : null);

  const imageUrl = coverMediaFileId ? buildCoverImageUrl(coverMediaFileId) : null;

  const reactionsSummary = p.reactionsSummary || p.reactions || {
    total: 0, like: 0, love: 0, care: 0, haha: 0, wow: 0, sad: 0, angry: 0,
  };

  const reactionUsersByType = p.reactionUsersByType || {
    like: [], love: [], care: [], haha: [], wow: [], sad: [], angry: [],
  };

  const reactionNamesByType = {};
  for (const key of ['like', 'love', 'care', 'haha', 'wow', 'sad', 'angry']) {
    const arr = Array.isArray(reactionUsersByType[key]) ? reactionUsersByType[key] : [];
    reactionNamesByType[key] = arr
      .map((u) => (u && typeof u.name === 'string' ? u.name.trim() : ''))
      .filter(Boolean);
  }

  const reactionNamesLegacy = Object.values(reactionNamesByType).flat();
  const isSavedFromBackend = p.isSaved ?? p.is_saved ?? p.saved ?? false;
  const isSavedForUser = isSavedFromBackend || savedIdsSet.has(String(p.id));

  return {
    id: p.id,
    slug: p.slug || null,
    authorName: p.postedAsName || currentUserName,
    authorAvatar: p.postedAsAvatarUrl || defaultAvatar,
    createdAt: p.createdAt || p.created_at || null,
    timeAgo: p.createdAt || '',
    body: p.content || p.body || '',
    backgroundColor: p.backgroundColor || p.background_color || null,
    backgroundKey: p.backgroundKey || p.backgroundColor || p.background_color || null,
    reactionsSummary,
    reactionsCount: Number(reactionsSummary.total) || 0,
    commentsCount: p.commentsCount ?? 0,
    sendCount: 0,
    reactionNamesByType,
    reactionNames: reactionNamesLegacy,
    hasMedia: attachments.length > 0 || !!coverMediaFileId,
    imageUrl,
    coverMediaFileId,
    attachments,
    poll: p.poll || p.pollData || p.postPoll || null,
    postType: p.postType || p.post_type || (p.poll ? 'poll' : 'text'),
    pollOptions: Array.isArray(p.pollOptions) ? p.pollOptions : null,
    currentUserName,
    currentUserAvatar,
    raw: p,
    isSaved: !!isSavedForUser,
    isPinned,
  };
}

async function loadPosts(options = {}) {
  const { append = false } = options;

  if (!props.featureInstanceId) {
    posts.value = [];
    offset.value = 0;
    hasMorePosts.value = false;
    hasLoadedOnce.value = true;
    return;
  }

  if (isLoadingPosts.value) return;

  isLoadingPosts.value = true;
  postsError.value = null;

  try {
    const currentOffset = append ? offset.value : 0;

    if (!append) {
      posts.value = [];
      offset.value = 0;
      hasMorePosts.value = true;
      hasLoadedOnce.value = false;
    }

    const params = new URLSearchParams({
      featureInstanceId: String(props.featureInstanceId),
      limit: String(limit),
      offset: String(currentOffset),
    });

    const savedParams = new URLSearchParams({
      featureInstanceId: String(props.featureInstanceId),
      limit: '50',
      offset: '0',
    });

    const [res, savedRes] = await Promise.all([
      fetch(`${apiProxyUrl}/api/creator-newsfeed/posts?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }),
      fetch(`${apiProxyUrl}/api/creator-newsfeed/saved-posts?${savedParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }),
    ]);

    if (!res.ok) throw new Error(`Failed to load posts (HTTP ${res.status})`);
    const data = await res.json();

    let savedIdsSet = new Set();
    if (savedRes && savedRes.ok) {
      const savedData = await savedRes.json().catch(() => ({}));
      const savedList = Array.isArray(savedData.posts) ? savedData.posts : [];
      savedIdsSet = new Set(savedList.map((sp) => String(sp.id)));
    }

    const list = Array.isArray(data.posts) ? data.posts : [];
    const mapped = list
      .filter((p) => {
        const status = String(p?.status || p?.postStatus || '').toLowerCase();
        return !status || status === 'published';
      })
      .map((p) => normalizeBackendPost(p, savedIdsSet));

    if (append) posts.value = posts.value.concat(mapped);
    else posts.value = mapped;

    // ✅ CRITICAL: measure stable offset + update window AFTER DOM update
    await nextTick();
    measurePostsOffsetTop();
    updateVisibleRange();

    if (list.length < limit) {
      hasMorePosts.value = false;
    } else {
      offset.value = currentOffset + limit;
      hasMorePosts.value = true;
    }
  } catch (err) {
    console.error('[NewsfeedCreatorDiscussionTab] loadPosts error:', err);
    postsError.value = err?.message || 'Failed to load posts';
  } finally {
    isLoadingPosts.value = false;
    hasLoadedOnce.value = true;
  }
}

/* ─────────────────────────────────────────────
 * 🔹 Delete flow
 *   1) handleDeletePost → open modal, store target id
 *   2) handleCancelDelete → close modal, clear target
 *   3) handleConfirmDelete → call backend + update list
 * ───────────────────────────────────────────*/

function scheduleLiveReload() {
  if (liveReloadTimer) {
    clearTimeout(liveReloadTimer);
    liveReloadTimer = null;
  }

  liveReloadTimer = setTimeout(() => {
    liveReloadTimer = null;
    loadPosts({ append: false });
  }, 200);
}

function sortPostsByPinnedAndCreated(list) {
  return [...(list || [])].sort((a, b) => {
    const aPinned = !!a?.isPinned;
    const bPinned = !!b?.isPinned;
    if (aPinned !== bPinned) return aPinned ? -1 : 1;

    const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
    return bTime - aTime;
  });
}

function mergeUpdatedPostWithExisting(existingPost, normalizedPost, rawUpdate = null) {
  const existing = existingPost || {};
  const normalized = normalizedPost || {};
  const raw = rawUpdate && typeof rawUpdate === 'object' ? rawUpdate : {};

  const merged = { ...existing, ...normalized };

  // Keep immutable/identity ordering fields if update payload is partial.
  if (!merged.createdAt) merged.createdAt = existing.createdAt || null;
  if (!merged.timeAgo) merged.timeAgo = existing.timeAgo || merged.createdAt || '';
  if (!merged.slug) merged.slug = existing.slug || null;
  if (!merged.authorName) merged.authorName = existing.authorName || null;
  if (!merged.authorAvatar) merged.authorAvatar = existing.authorAvatar || null;

  // If backend update payload did not include usable media fields, preserve current media state.
  const hasRawAttachments = Array.isArray(raw.attachments);
  const hasUsableRawAttachments =
    hasRawAttachments &&
    raw.attachments.some((att) => {
      if (!att || typeof att !== 'object') return false;
      // fileId-only attachment objects are common in edit payloads and should not
      // be treated as a full media replacement.
      return !!(
        att.url ||
        att.fileUrl ||
        att.file_url ||
        att.videoUrl ||
        att.secureVideoUrl ||
        att.hlsPlaylistUrl ||
        att.hls_playlist_url ||
        att.thumbnailUrl ||
        att.posterUrl ||
        att.mimeType ||
        att.mime ||
        att.contentType ||
        att.fileType ||
        att.type
      );
    });
  const rawCoverMediaValue =
    raw.coverMediaFileId ??
    raw.cover_media_file_id ??
    null;
  const existingCoverMediaValue = existing.coverMediaFileId ?? null;
  const hasRawCoverMedia =
    (Object.prototype.hasOwnProperty.call(raw, 'coverMediaFileId') ||
      Object.prototype.hasOwnProperty.call(raw, 'cover_media_file_id')) &&
    String(rawCoverMediaValue || '') !== String(existingCoverMediaValue || '');
  const hasRawMediaAdds = Array.isArray(raw.mediaAddFileIds) && raw.mediaAddFileIds.length > 0;
  const hasRawMediaRemoves =
    Array.isArray(raw.mediaRemoveFileIds) && raw.mediaRemoveFileIds.length > 0;
  const hasAnyRawMediaMutation =
    hasRawCoverMedia ||
    hasUsableRawAttachments ||
    hasRawMediaAdds ||
    hasRawMediaRemoves;

  const normalizedHasMedia =
    !!normalized.coverMediaFileId ||
    !!normalized.imageUrl ||
    (Array.isArray(normalized.attachments) && normalized.attachments.length > 0) ||
    !!normalized.hasMedia;
  const existingHasMedia =
    !!existing.coverMediaFileId ||
    !!existing.imageUrl ||
    (Array.isArray(existing.attachments) && existing.attachments.length > 0) ||
    !!existing.hasMedia;

  if (!hasAnyRawMediaMutation || (!normalizedHasMedia && existingHasMedia && !hasRawMediaRemoves)) {
    merged.attachments = Array.isArray(existing.attachments) ? existing.attachments : [];
    merged.coverMediaFileId = existing.coverMediaFileId || null;
    merged.imageUrl = existing.imageUrl || null;
    merged.hasMedia =
      !!existing.hasMedia ||
      !!existing.coverMediaFileId ||
      (Array.isArray(existing.attachments) && existing.attachments.length > 0);
  }

  // Preserve poll state when update payload does not explicitly mutate poll.
  const hasRawPollMutation =
    Object.prototype.hasOwnProperty.call(raw, 'poll') ||
    Object.prototype.hasOwnProperty.call(raw, 'postType') ||
    Object.prototype.hasOwnProperty.call(raw, 'post_type') ||
    Object.prototype.hasOwnProperty.call(raw, 'pollOptions') ||
    Object.prototype.hasOwnProperty.call(raw, 'poll_options');
  const normalizedHasPoll =
    !!normalized.poll ||
    String(normalized.postType || '').toLowerCase() === 'poll' ||
    (Array.isArray(normalized.pollOptions) && normalized.pollOptions.length > 0);
  const existingHasPoll =
    !!existing.poll ||
    String(existing.postType || '').toLowerCase() === 'poll' ||
    (Array.isArray(existing.pollOptions) && existing.pollOptions.length > 0);

  if (!hasRawPollMutation || (!normalizedHasPoll && existingHasPoll)) {
    merged.poll = existing.poll || null;
    merged.postType = existing.postType || merged.postType || null;
    merged.pollOptions = Array.isArray(existing.pollOptions)
      ? existing.pollOptions
      : merged.pollOptions || null;
  }

  return merged;
}

async function upsertSinglePostBySlug(slug) {
  const safeSlug = String(slug || '').trim();
  if (!safeSlug) return false;

  const res = await fetch(
    `${apiProxyUrl}/api/creator-newsfeed/posts/by-slug/${encodeURIComponent(safeSlug)}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`fetch by slug failed (${res.status}) ${txt}`);
  }

  const data = await res.json().catch(() => ({}));
  const backendPost = data?.post || null;
  if (!backendPost || !backendPost.id) return false;
  const backendStatus = String(backendPost.status || backendPost.postStatus || '').toLowerCase();
  if (backendStatus && backendStatus !== 'published') return false;

  const normalized = normalizeBackendPost(backendPost, new Set());
  const existingIdx = posts.value.findIndex((p) => String(p.id) === String(normalized.id));

  if (existingIdx >= 0) {
    const next = [...posts.value];
    next[existingIdx] = { ...next[existingIdx], ...normalized };
    posts.value = sortPostsByPinnedAndCreated(next);
  } else {
    posts.value = sortPostsByPinnedAndCreated([normalized, ...posts.value]);
  }

  await nextTick();
  measurePostsOffsetTop();
  updateVisibleRange();
  return true;
}

async function addOrUpdatePostFromCreate(newPost) {
  const post = newPost && typeof newPost === 'object' ? newPost : null;
  if (!post) return false;

  const currentInstanceId = Number(props.featureInstanceId);
  const incomingInstanceId = Number(
    post.instanceId ?? post.featureInstanceId ?? post.instance_id
  );

  if (
    Number.isFinite(currentInstanceId) &&
    Number.isFinite(incomingInstanceId) &&
    currentInstanceId !== incomingInstanceId
  ) {
    return false;
  }

  const status = String(post.status || post.postStatus || '').toLowerCase();
  if (status && status !== 'published') {
    return false;
  }

  const slug = String(post.slug || '').trim();
  if (slug) {
    await upsertSinglePostBySlug(slug);
    return true;
  }

  const normalized = normalizeBackendPost(post, new Set());
  if (!normalized?.id) return false;

  const idx = posts.value.findIndex((p) => String(p.id) === String(normalized.id));
  if (idx >= 0) {
    const next = [...posts.value];
    next[idx] = mergeUpdatedPostWithExisting(next[idx], normalized, post);
    posts.value = sortPostsByPinnedAndCreated(next);
  } else {
    posts.value = sortPostsByPinnedAndCreated([normalized, ...posts.value]);
  }

  await nextTick();
  measurePostsOffsetTop();
  updateVisibleRange();
  return true;
}

async function addOrUpdatePostFromUpdate(updatedPost) {
  const post = updatedPost && typeof updatedPost === 'object' ? updatedPost : null;
  if (!post) return false;

  const status = String(post.status || post.postStatus || '').toLowerCase();
  // For update events, do not eagerly remove from local list on non-published
  // statuses. Let caller trigger a full reload so transient/stale status values
  // do not make edited posts disappear from the feed.
  if (status && status !== 'published') return false;

  const slug = String(post.slug || '').trim();
  if (slug) {
    await upsertSinglePostBySlug(slug);
    return true;
  }

  const normalized = normalizeBackendPost(post, new Set());
  if (!normalized?.id) return false;

  const idx = posts.value.findIndex((p) => String(p.id) === String(normalized.id));
  if (idx >= 0) {
    const next = [...posts.value];
    next[idx] = mergeUpdatedPostWithExisting(next[idx], normalized, post);
    posts.value = sortPostsByPinnedAndCreated(next);
  } else {
    posts.value = sortPostsByPinnedAndCreated([normalized, ...posts.value]);
  }

  await nextTick();
  measurePostsOffsetTop();
  updateVisibleRange();
  return true;
}

async function handleWsPostCreated(payload) {
  if (!payload) return;
  const status = String(payload.status || '').toLowerCase();
  if (status && status !== 'published') return;

  const currentInstanceId = Number(props.featureInstanceId);
  const incomingInstanceId = Number(payload.instanceId);
  if (!Number.isFinite(currentInstanceId) || !Number.isFinite(incomingInstanceId)) return;
  if (currentInstanceId !== incomingInstanceId) return;

  const postId = payload.postId != null ? String(payload.postId) : '';
  if (postId && seenWsPostIds.has(postId)) return;
  if (postId) seenWsPostIds.add(postId);

  let upserted = false;
  try {
    upserted = await upsertSinglePostBySlug(payload.slug);
  } catch (err) {
    console.error('[NewsfeedCreatorDiscussionTab] upsertSinglePostBySlug error:', err);
  }

  if (!upserted) {
    scheduleLiveReload();
  }
}

function handleWsCommentCreated(payload) {
  if (!payload) return;

  const currentInstanceId = Number(props.featureInstanceId);
  const incomingInstanceId = Number(payload.instanceId);
  if (!Number.isFinite(currentInstanceId) || !Number.isFinite(incomingInstanceId)) return;
  if (currentInstanceId !== incomingInstanceId) return;

  const commentId = payload.commentId != null ? String(payload.commentId) : '';
  if (commentId && seenWsCommentIds.has(commentId)) return;
  if (commentId) seenWsCommentIds.add(commentId);

  const targetPostId = payload.postId != null ? String(payload.postId) : '';
  if (!targetPostId) {
    scheduleLiveReload();
  } else {
    let updated = false;
    posts.value = posts.value.map((p) => {
      if (String(p.id) !== targetPostId) return p;
      updated = true;
      const current = Number(p.commentsCount) || 0;
      return { ...p, commentsCount: current + 1 };
    });

    if (!updated) {
      scheduleLiveReload();
    }
  }

}

function handleWsPostDeleted(payload) {
  if (!payload) return;

  const currentInstanceId = Number(props.featureInstanceId);
  const incomingInstanceId = Number(payload.instanceId);
  if (!Number.isFinite(currentInstanceId) || !Number.isFinite(incomingInstanceId)) return;
  if (currentInstanceId !== incomingInstanceId) return;

  const targetPostId = String(payload.postId || '');
  if (!targetPostId) return;

  posts.value = posts.value.filter((post) => String(post.id) !== targetPostId);
  if (String(selectedFullPost.value?.id || '') === targetPostId) {
    isFullPostOpen.value = false;
    selectedFullPost.value = null;
  }
}

function handleWsCommentDeleted(payload) {
  if (!payload) return;

  const currentInstanceId = Number(props.featureInstanceId);
  const incomingInstanceId = Number(payload.instanceId);
  if (!Number.isFinite(currentInstanceId) || !Number.isFinite(incomingInstanceId)) return;
  if (currentInstanceId !== incomingInstanceId) return;

  const targetPostId = payload.postId != null ? String(payload.postId) : '';
  if (!targetPostId) return;

  const decBy = Math.max(1, Number(payload.deletedCount || 0));
  let updated = false;
  posts.value = posts.value.map((p) => {
    if (String(p.id) !== targetPostId) return p;
    updated = true;
    const current = Number(p.commentsCount) || 0;
    return { ...p, commentsCount: Math.max(0, current - decBy) };
  });

  if (!updated) {
    scheduleLiveReload();
  }
}

async function handleWsPostReactionUpdated(payload) {
  if (!payload) return;

  const currentInstanceId = Number(props.featureInstanceId);
  const incomingInstanceId = Number(payload.instanceId);
  if (!Number.isFinite(currentInstanceId) || !Number.isFinite(incomingInstanceId)) return;
  if (currentInstanceId !== incomingInstanceId) return;

  const dedupeKey = `${String(payload.postId || '')}:${String(payload.createdAt || '')}`;
  if (dedupeKey !== ':' && seenWsPostReactionKeys.has(dedupeKey)) return;
  if (dedupeKey !== ':') seenWsPostReactionKeys.add(dedupeKey);

  const safeSlug = String(payload.slug || '').trim();
  if (safeSlug) {
    const upserted = await upsertSinglePostBySlug(safeSlug);
    if (!upserted) {
      scheduleLiveReload();
      return;
    }
    return;
  }

  scheduleLiveReload();
}

async function openPostBySlug(slug) {
  const safeSlug = String(slug || '').trim();
  if (!safeSlug) return;
  if (isLoadingDeepLinkPost.value) return;

  const alreadyOpen =
    isFullPostOpen.value &&
    String(selectedFullPost.value?.slug || '').trim() === safeSlug;
  if (alreadyOpen || lastOpenedSlug.value === safeSlug) return;

  isLoadingDeepLinkPost.value = true;
  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/by-slug/${encodeURIComponent(safeSlug)}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      console.error('[NewsfeedCreatorDiscussionTab] openPostBySlug failed:', res.status, txt);
      return;
    }

    const data = await res.json().catch(() => ({}));
    const backendPost = data?.post || null;
    if (!backendPost || !backendPost.id) return;

    selectedFullPost.value = normalizeBackendPost(backendPost, new Set());
    isFullPostOpen.value = true;
    lastOpenedSlug.value = safeSlug;
    trackPostView(selectedFullPost.value?.id, 'slug');
  } catch (err) {
    console.error('[NewsfeedCreatorDiscussionTab] openPostBySlug error:', err);
  } finally {
    isLoadingDeepLinkPost.value = false;
  }
}

// from child @delete-post
const handleEditPost = ({ postId }) => {
  if (!postId) return;

  const postToEdit = posts.value.find((p) => String(p.id) === String(postId));
  if (!postToEdit) {
    console.warn('[NewsfeedCreatorDiscussionTab] edit requested but post not found:', postId);
    return;
  }

  emit('open-edit-post', { post: { ...postToEdit } });
};

const handleDeletePost = ({ postId }) => {
  if (!postId) return;
  deleteTargetPostId.value = postId;
  deleteModalOpen.value = true;
};

const handleCancelDelete = () => {
  deleteModalOpen.value = false;
  deleteTargetPostId.value = null;
  isDeletingPost.value = false;
};

const handleConfirmDelete = async () => {
  const postId = deleteTargetPostId.value;
  if (!postId) return;

  isDeletingPost.value = true;

  try {
    const resp = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${postId}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const data = await resp.json().catch(() => ({}));

    if (!resp.ok || !data.success) {
      console.error(
        '[NewsfeedCreatorDiscussionTab] delete post failed:',
        resp.status,
        data
      );
      // optional: toast / UI message
      return;
    }

    // Remove from local list so UI updates immediately
    posts.value = posts.value.filter(
      (p) => String(p.id) !== String(postId)
    );

    // close modal
    deleteModalOpen.value = false;
    deleteTargetPostId.value = null;
  } catch (err) {
    console.error('[NewsfeedCreatorDiscussionTab] error deleting post:', err);
    // optional: toast
  } finally {
    isDeletingPost.value = false;
  }
};


// Helper: we only want to load posts once we know
// 1) which instance we're on, AND
// 2) what avatar we're "posting as" for that instance.
const canLoadForCurrentState = () =>
  !!props.featureInstanceId && !!props.postingAsAvatarUrl;

const handleSavePostFromDiscussion = ({ postId }) => {
  if (!postId) return;

  posts.value = posts.value.map((p) =>
    String(p.id) === String(postId)
      ? { ...p, isSaved: true }
      : p
  );
};


const handleUnsavePostFromDiscussion = async ({ postId }) => {
  if (!postId) return;

  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(
        postId
      )}/save`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.error(
        '[NewsfeedCreatorDiscussionTab] unsave backend NOT OK:',
        res.status,
        text
      );
      // optional: toast here
      return;
    }

    // Optional: we don't actually need the body here,
    // but it won't hurt to consume it.
    await res.json().catch(() => ({}));

    // mark this post as not saved so the menu flips back to "Save post"
    posts.value = posts.value.map((p) =>
      String(p.id) === String(postId)
        ? { ...p, isSaved: false }
        : p
    );
  } catch (err) {
    console.error(
      '[NewsfeedCreatorDiscussionTab] error unsaving post:',
      err
    );
  }
};

const handlePinChanged = ({ postId, isPinned }) => {
  if (!postId) return;

  // 1) update the pinned flag on the matching post
  const updated = posts.value.map((p) =>
    String(p.id) === String(postId)
      ? { ...p, isPinned: !!isPinned }
      : p
  );

  // 2) re-sort to match backend ORDER BY:
  //    - pinned first
  //    - then newest (createdAt) first
  updated.sort((a, b) => {
    if (a.isPinned !== b.isPinned) {
      return a.isPinned ? -1 : 1; // true first
    }

    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;

    return bTime - aTime; // newest first
  });

  posts.value = updated;
};


let postsResizeObserver = null;

const setupPostsResizeObserver = () => {
  if (!postsListRef.value) return;

  if (postsResizeObserver) {
    postsResizeObserver.disconnect();
    postsResizeObserver = null;
  }

  postsResizeObserver = new ResizeObserver(() => {
    // Layout changed (images loaded, etc.)
    // Re-measure + update window
    measurePostsOffsetTop();
    updateVisibleRange();
  });

  postsResizeObserver.observe(postsListRef.value);
};


/* ─────────────────────────────────────────────
 * ✅ Infinite scroll (correct root)
 * ───────────────────────────────────────────*/
const infiniteScrollSentinel = ref(null);
let infiniteObserver = null;

const setupInfiniteScrollObserver = () => {

    if (!scrollContainerEl.value) {
  scrollContainerEl.value = document.querySelector('[data-creator-main-scroll]');
}

  if (!infiniteScrollSentinel.value) return;

  if (infiniteObserver) {
    infiniteObserver.disconnect();
    infiniteObserver = null;
  }

  infiniteObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (!entry || !entry.isIntersecting) return;

      if (!hasMorePosts.value) return;
      if (isLoadingPosts.value) return;
      if (!posts.value.length) return;

      if (isPaging) return;
        isPaging = true;

        loadPosts({ append: true }).finally(() => {
          isPaging = false;
        });

    },
    {
      // ✅ IMPORTANT: root must be the scrolling container, not the viewport
      root: scrollContainerEl.value || null,
      rootMargin: '0px 0px 600px 0px',
      threshold: 0.01,
    }
  );

  infiniteObserver.observe(infiniteScrollSentinel.value);
};

watch(
  () => infiniteScrollSentinel.value,
  (el) => {
    if (el) setupInfiniteScrollObserver();
    else if (infiniteObserver) {
      infiniteObserver.disconnect();
      infiniteObserver = null;
    }
  }
);

// Clean up on destroy
onBeforeUnmount(() => {
  if (unsubscribeNewsfeedPostCreated) {
    unsubscribeNewsfeedPostCreated();
    unsubscribeNewsfeedPostCreated = null;
  }
  if (unsubscribeNewsfeedCommentCreated) {
    unsubscribeNewsfeedCommentCreated();
    unsubscribeNewsfeedCommentCreated = null;
  }
  if (unsubscribeNewsfeedPostDeleted) {
    unsubscribeNewsfeedPostDeleted();
    unsubscribeNewsfeedPostDeleted = null;
  }
  if (unsubscribeNewsfeedCommentDeleted) {
    unsubscribeNewsfeedCommentDeleted();
    unsubscribeNewsfeedCommentDeleted = null;
  }
  if (unsubscribeNewsfeedPostReactionUpdated) {
    unsubscribeNewsfeedPostReactionUpdated();
    unsubscribeNewsfeedPostReactionUpdated = null;
  }
  if (unsubscribeNewsfeedPostReactionToast) {
    unsubscribeNewsfeedPostReactionToast();
    unsubscribeNewsfeedPostReactionToast = null;
  }
  if (liveReloadTimer) {
    clearTimeout(liveReloadTimer);
    liveReloadTimer = null;
  }

  if (infiniteObserver) {
    infiniteObserver.disconnect();
    infiniteObserver = null;
  }

  if (scrollContainerEl.value) {
    scrollContainerEl.value.removeEventListener(
      'scroll',
      handleScrollForVirtualization
    );
  }

  if (postsResizeObserver) {
  postsResizeObserver.disconnect();
  postsResizeObserver = null;
}

  if (rafScrollHandle) {
    cancelAnimationFrame(rafScrollHandle);
    rafScrollHandle = 0;
  }
  if (mediaRaf) {
    cancelAnimationFrame(mediaRaf);
    mediaRaf = 0;
  }
  if (visibleRangeTimer) {
    window.clearTimeout(visibleRangeTimer);
    visibleRangeTimer = 0;
  }


});




// Initial load: only load if instance + avatar are ready
onMounted(() => {
  if (canLoadForCurrentState()) {
    loadPosts();
  }
  loadAboutDescription();

  unsubscribeNewsfeedPostCreated = websocketStore.subscribe(
    'newsfeed:postCreated',
    (payload) => handleWsPostCreated(payload)
  );
  unsubscribeNewsfeedPostDeleted = websocketStore.subscribe(
    'newsfeed:postDeleted',
    (payload) => handleWsPostDeleted(payload)
  );
  unsubscribeNewsfeedCommentCreated = websocketStore.subscribe(
    'newsfeed:commentCreated',
    (payload) => handleWsCommentCreated(payload)
  );
  unsubscribeNewsfeedCommentDeleted = websocketStore.subscribe(
    'newsfeed:commentDeleted',
    (payload) => handleWsCommentDeleted(payload)
  );
  unsubscribeNewsfeedPostReactionUpdated = websocketStore.subscribe(
    'newsfeed:postReactionUpdated',
    (payload) => handleWsPostReactionUpdated(payload)
  );
  unsubscribeNewsfeedPostReactionToast = websocketStore.subscribe(
    'newsfeed:postReactionToast',
    (payload) => handleWsPostReactionUpdated(payload)
  );
});

// Setup virtualization scroll listener
onMounted(() => {
  // Main scroll container in CreatorLayout.vue
  scrollContainerEl.value = document.querySelector('[data-creator-main-scroll]');

  if (scrollContainerEl.value) {
    scrollContainerEl.value.addEventListener(
      'scroll',
      handleScrollForVirtualization,
      { passive: true }
    );

    // After DOM is ready, measure offset and set initial visible window
    nextTick(() => {
      measurePostsOffsetTop();
      updateVisibleRange();
      setupInfiniteScrollObserver();
      setupPostsResizeObserver();
    });
  }
});

watch(
  () => useVirtualization.value,
  (newVal) => {
    if (!newVal) {
      // Turning virtualization OFF → show everything
      visibleRange.value = { start: 0, end: posts.value.length };
      return;
    }

    // Turning virtualization ON → re-measure + recalc
    nextTick(() => {
      measurePostsOffsetTop();
      updateVisibleRange();
    });
  }
);


// When the instance changes, just clear posts immediately.
// DO NOT load here; wait for the avatar to update.
watch(
  () => props.featureInstanceId,
  (newId, oldId) => {
    if (newId === oldId) return;

    if (!newId) {
      posts.value = [];
      offset.value = 0;
      hasMorePosts.value = false;
      hasLoadedOnce.value = true;
      aboutDescription.value = '';
      aboutDraft.value = '';
      isEditingAbout.value = false;
      aboutError.value = '';
      return;
    }
    posts.value = [];
    offset.value = 0;
    hasMorePosts.value = true;
    hasLoadedOnce.value = false;
    lastOpenedSlug.value = '';
    trackedPostViewKeys.clear();
    loadAboutDescription();
    // ✅ Rebuild noted observer & visible window
    nextTick(() => {
      measurePostsOffsetTop();
      updateVisibleRange();
      setupInfiniteScrollObserver();
    });
  }
);
// When the posting-as avatar changes (parent finished instance-settings),
// and we have a selected instance, THEN load posts.
watch(
  () => props.postingAsAvatarUrl,
  (newAvatar) => {
    if (!newAvatar || !props.featureInstanceId) return;

    loadPosts();
  }
);

watch(
  () => props.openPostSlug,
  (slug) => {
    if (!slug) return;
    openPostBySlug(slug);
  },
  { immediate: true }
);

// Whenever number of posts changes, recompute virtualization window
watch(
  () => posts.value.length,
  () => {
    nextTick(() => {
      measurePostsOffsetTop();
      updateVisibleRange();
    });
  }
);

watch(
  () => visiblePosts.value.length,
  (len) => {
    console.log('[DiscussionTab] visiblePosts length:', len, {
      totalPosts: posts.value.length,
      useVirtualization: useVirtualization.value,
      range: visibleRange.value,
    });
  }
);



// Expose manual reload for parent (after post created)
defineExpose({
  reloadPosts: () => loadPosts({ append: false }),
  addOrUpdatePostFromCreate,
  addOrUpdatePostFromUpdate,
});


</script>

<style scoped>
/* ✅ Allow dropdown menus / tooltips to escape virtual-scroller wrappers */
:deep(.discussionScroller.vue-recycle-scroller),
:deep(.discussionScroller .vue-recycle-scroller),
:deep(.discussionScroller .vue-recycle-scroller__item-wrapper),
:deep(.discussionScroller .vue-recycle-scroller__item-view) {
  overflow: visible !important;
}

/* ─────────────────────────────────────────────
   Creator layout scrollbar (right column only)
   ─────────────────────────────────────────── */

/* Firefox base */
.app-scroll {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  /* try to keep layout width stable */
  scrollbar-gutter: stable;
}

/* WebKit base (Chrome / Edge / Safari) */
.app-scroll::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

/* Hide old-school buttons/arrows (where they exist) */
.app-scroll::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
}

/* Track fully invisible so it looks “on top” of content */
.app-scroll::-webkit-scrollbar-track {
  background: transparent;
}

/* Thumb invisible by default */
.app-scroll::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 999px;
}

/* On hover over the scroll area: fade thumb in */
.app-scroll:hover {
  scrollbar-color: rgba(145, 150, 156, 0.541) transparent; /* Firefox */
}

.app-scroll:hover::-webkit-scrollbar-thumb {
  background-color: rgba(140, 143, 148, 0.479);
}

/* Slightly stronger when directly hovering the thumb */
.app-scroll:hover::-webkit-scrollbar-thumb:hover {
  background-color: rgba(203, 213, 225, 0.65);
}

</style>




